// R6-1 — THE ROUTE MAP, MOVED VERBATIM OUT OF App.tsx.
//
// Every page id, its URL, and the one function that turns a browser pathname back into a page. It
// left App.tsx first because everything else queued for extraction is typed by `Page`: the menu
// catalogs are `page: Page`, the generators are `Exclude<Page, …>`, and a type cannot be imported
// from a file that also holds 17,000 lines of component.
//
// This module imports NOTHING, which is the entry requirement for `scripts/r6-catalogs.mjs` to load
// and RUN it instead of reading it.

type Page =
  | "home"
  | "my-room"
  | "library"
  | "astrology"
  | "numerology"
  | "dreams"
  | "tarot"
  | "human-design"
  | "astrocartography"
  | "akashic"
  | "palmistry"
  | "daily"
  | "spell-book"
  | "seals"
  | "ask-lala"
  | "living-room"
  // ITEM 5: the inbox is a SCREEN of its own now, not a sheet over the room.
  | "inbox"
  | "terms"
  | "privacy"
  | "faq"
  | "contact"
  | "dashboard"
  | "membership"
  // HER CONVERSION SURFACE 2 (2026-08-18): the signup page her artwork IS. Every signup and
  // login trigger in the house lands here, so it is deliberately ON no menu -- see OFF_MENU_PAGES.
  | "signup"
  // F1: her lead-collection landing page. Deliberately unreachable from inside the site — see
  // OFF_MENU_PAGES below, where that absence is DECLARED rather than merely true.
  | "funnel"
  // F4-1: her ASK LALA door, named clean for ads. It is a PAGE and deliberately NOT a fourth
  // `FUNNEL_KEYS` entry — a key on that roster captures a lead and delivers a paid READING by
  // email, and her clause 2 for this funnel is SIGNUP FIRST. The reasoning is in src/talkDoor.js.
  | "talk"
  // AUTH-BREACH-1 — the box a claim link opens. Direct URL only, carries a one-time token.
  | "claim";

export type { Page };

// HP6: REAL ROUTES. The page used to live only in React state, so a refresh had nothing to
// restore from and always dumped you back home, the back button did nothing, and no page could
// be linked or bookmarked. Every page now owns a URL.
// The server already serves index.html for unknown non-API paths, so a deep link and a refresh
// both land on the app, which then reads the path below.
// One map, both directions: a page cannot have a URL that does not route back to it.
export const PAGE_PATHS: Record<Page, string> = {
  home: "/",
  "my-room": "/my-room",
  library: "/library",
  astrology: "/astrology",
  numerology: "/numerology",
  dreams: "/dreams",
  tarot: "/tarot",
  "human-design": "/human-design",
  astrocartography: "/astrocartography",
  akashic: "/akashic",
  palmistry: "/palmistry",
  daily: "/daily",
  "spell-book": "/magic-room",
  seals: "/seals",
  "ask-lala": "/ask-lala",
  "living-room": "/living-room",
  inbox: "/inbox",
  terms: "/terms",
  privacy: "/privacy",
  faq: "/faq",
  contact: "/contact",
  dashboard: "/dashboard",
  membership: "/membership",
  signup: "/signup",
  funnel: "/funnel",
  talk: "/talk",
  claim: "/claim",
};
export const PATH_PAGES: Record<string, Page> = Object.fromEntries(
  Object.entries(PAGE_PATHS).map(([p, url]) => [url, p as Page]),
) as Record<string, Page>;
// An unknown path is home, not a crash: a stale or mistyped link should land somewhere real.
export function pageFromPath(pathname: string): Page {
  const clean = (pathname || "/").replace(/\/+$/, "") || "/";
  return PATH_PAGES[clean] || "home";
}

// W1-1 — WHICH PAGE COSTS WHICH FEATURE. THE MAP THAT MAKES "LOCKED MEANS CLOSED" A ROUTING RULE.
//
// Her finding was four surfaces opening behind the tier box, and the deepest form of it was here:
// navigation had NO gate at all. `goTo("seals")` mounted the Seal Creator for anybody, and the only
// refusal was the generator's own, after she had already filled the form in. So the check moves to
// the door: below tier, the tap raises the box and the page is never entered.
//
// The keys are `server/tiers.js` FEATURES keys and the gate holds them to that file, so a feature
// renamed on the server cannot leave a page silently ungated here. A page that is NOT in this map is
// deliberately open to everyone (home, my-room, the legal pages, membership, the free readings) —
// stated by absence, and the gate names every one of them so the absence is a decision and not an
// oversight.
export const PAGE_FEATURE: Partial<Record<Page, string>> = {
  library: "library",
  astrology: "room:astrology",
  numerology: "room:numerology",
  dreams: "room:dream",
  tarot: "room:tarot",
  "human-design": "room:human-design",
  astrocartography: "room:astrocartography",
  akashic: "room:akashic",
  palmistry: "room:palmistry",
  "spell-book": "room:magic",
  seals: "room:seal-creator",
  "ask-lala": "lala:chat",
  // R23: `daily` USED TO BE PRICED HERE, at `daily:guidance` (newbie), and that was the one page on
  // this site whose router arm does not exist — it falls through to `GeneratorPage`. So below tier
  // the tap raised the purple box and above tier the room said "This room is coming soon": a member
  // was asked to pay for a room that then told her it was not built. Nothing built lost a gate by
  // taking this line out. The daily guidance that IS built is the My Room panel served by
  // `/api/room/daily`, and server/route-features.js still holds that route at newbie.
  // LR1 — the community room is APPRENTICE by her word. The key is resolved in server/tiers.js and
  // written down nowhere else, so a tier move is one line there and this map never needs touching.
  "living-room": "room:living",
  // ITEM 5: the inbox is the community room's own messaging, so it costs the room's own key. One
  // declaration in server/tiers.js still decides the tier for both.
  inbox: "room:living",
};

// The pages that cost nothing, named rather than implied. Read by the gate against PAGE_PATHS, so a
// NEW page must be classified one way or the other before it can ship.
export const OPEN_PAGES: Page[] = [
  "home", "my-room", "terms", "privacy", "faq", "contact", "dashboard", "membership", "daily",
  // F1: a landing page for paid traffic. It costs NOTHING and must not — a tier box in front of an
  // advertisement is the click she paid for, thrown away at the door.
  "funnel",
  // HER SIGNUP PAGE: it costs NOTHING and must not. A tier box between a stranger and an
  // ACCOUNT is her own ruling inverted -- desire, account, then price.
  "signup",
  // F4-1 — her ad door, free for the funnel’s own reason: a tier box in front of an advertisement is
  // the click she paid for, thrown away before the stranger reads a word.
  "talk",
  // AUTH-BREACH-1: the box where a locked-out member sets her password. A tier box in front of
  // SECURING AN ACCOUNT would be this house charging a member to get back into her own room.
  "claim",
];

// F1 — **THE PAGES THAT ARE DELIBERATELY IN NO MENU, NAMED RATHER THAN MERELY ABSENT.**
//
// Her clause is *"NOT REACHABLE FROM THE SITE — no menu entry, no internal links, direct URL only"*,
// and this house holds three separate biconditionals over what a member can reach: `EVERY_MENU_ROW`
// (r22's eighteen rows, held by name in BOTH menus), `rooms.tsx` (the home cards and the side menu)
// and R23's router law. **A page that is in none of them is exactly the shape those rosters exist to
// catch** — so the funnel is DECLARED here with its reason, and the absence becomes a decision
// somebody made rather than a row somebody forgot.
//
// **THE FAILURE THIS PREVENTS IS AN INSTRUMENT GOING RED ABOUT A PAGE BEHAVING EXACTLY AS SHE
// ORDERED**, which is this repo's most-repeated defect; the cheapest possible moment to avoid it is
// before the page exists, which is where this line was written.
//
// It is NOT the same question as `OPEN_PAGES` (what a page COSTS) or `COMING_SOON_PAGES` (whether it
// is BUILT). A page may be free, built, routable and still deliberately unlisted — that is this one.
export const OFF_MENU_PAGES: Page[] = ["funnel", "signup", "talk", "claim"];

/**
 * ================================================================================================
 * **ITEM 2, HER DEFECT 4 — THE PAGES THAT CARRY THEIR OWN WAY OUT.**
 *
 * F1's safety clause is that **a standalone page is off every menu**, and its own comment names the
 * reason in as many words: *"a page with no navigation on it that a member can still REACH from a
 * menu is a room with the door bricked up — she taps in and has no way out but the back button."*
 *
 * >>> **HER CLAUSE 4 GAVE THE MEMBERSHIP PAGE EXACTLY THAT WAY OUT, SO F1's PREMISE IS MEASURABLY
 * FALSE FOR IT AND STILL TRUE FOR THE OTHER TWO (DL-2/DT-2b/R181).** <<< Her words: *"a BACK button
 * (house-styled, gold) returning them to wherever they came from... nobody stuck on the page."* Left
 * standing, F1's clause would have been RED about the page she ordered — this repo's most-repeated
 * instrument defect — so it is MIGRATED at its declaration rather than weakened, and what replaces it
 * is **strictly stronger**: what is forbidden is *reachable from a menu AND no way out*, which is the
 * conjunction its own comment describes and which neither roster alone could ever have expressed.
 *
 * **AND IT IS A DECLARATION HELD AGAINST THE COMPONENTS RATHER THAN A PROMISE (R11/R31).** A page
 * named here that renders no return control is the dead end wearing a roster entry, which is exactly
 * what a list nobody crosses against the code becomes. F1 asks the surface.
 */
export const RETURN_CAPABLE_PAGES: Page[] = ["membership"];

/**
 * ================================================================================================
 * **L1 (her live-test order, 2026-08-13) — THE PAGES THAT WEAR NO SITE CHROME AT ALL.**
 *
 * Her report, off her daughter's real visit: **on DESKTOP the funnel rendered INSIDE the site, with
 * the full side menu.** Her ruling: `/funnel` is its own landing page at **every** viewport — no side
 * menu, no top bar, no bottom nav, **no site navigation at all**. The page is the hook, the form, the
 * reading, the bridge, nothing else.
 *
 * **IT IS A THIRD, SEPARATE QUESTION AND IT IS NOT `OFF_MENU_PAGES` (R18).** That roster answers
 * *can a member REACH this page from inside the house* — a fact about the menus. This one answers
 * *does this page WEAR the house* — a fact about the shell. They agree today on one page and they are
 * not the same law: a page could legitimately be off every menu and still be a normal room somebody
 * arrives at by link (the daily line's landing is exactly that shape), and collapsing them would make
 * the next unlisted room render with no way out of it.
 *
 * **THE CHROME IS NOT RENDERED, RATHER THAN HIDDEN BY A RULE**, which is `siteFooterHidden`'s own
 * ruling one component over: a hidden bar still occupies the document, still takes the tab order and
 * still answers a hit test — and a stranger who tabs into a side menu that is painted out is a
 * stranger the page just lost. It is also why this is asked in App.tsx's SHELL rather than inside
 * each control: twelve controls each remembering is eleven chances for the next one to forget.
 * ================================================================================================
 */
export const STANDALONE_PAGES: Page[] = ["membership", "funnel", "talk", "claim"];

/** Does this page render bare, with none of the house's navigation around it? */
export const pageIsStandalone = (page: Page): boolean => STANDALONE_PAGES.includes(page);

// R23 — THE PAGES THAT HAVE NO ROUTER ARM, NAMED RATHER THAN IMPLIED, and for the reason OPEN_PAGES
// is named: a page nobody classified is a page nobody decided about. Every OTHER page in PAGE_PATHS
// is branched on by `page === "…"` in App.tsx; these fall through to `GeneratorPage`, which tells the
// member the room is coming soon.
//
// THE LAW WITH TEETH, and it is the one the fallthrough's own comment states and the routing map was
// breaking: **A PAGE IN HERE MAY NOT COST A FEATURE.** A tier box in front of a door that opens onto
// "coming soon" is exactly the subscribe-to-unlock promise that component refuses by name.
// `scripts/r23-page-router.mjs` runs both halves — that this set is exactly the unbranched pages, and
// that none of them is priced.
export const COMING_SOON_PAGES: Page[] = ["daily"];

export const featureForPage = (page: Page): string | null => PAGE_FEATURE[page] || null;

// R42 — WHAT THE PAGE IS CALLED WHEN IT IS NOT ON THE SCREEN.
//
// `document.title` was assigned NOWHERE in this repo. index.html carries one static <title> and this
// is a client-routed SPA, so every one of the 21 pages above wore the same 34 characters everywhere a
// browser writes a page's name down: the tab, the bookmark, the history list, Add to Home Screen, and
// **the iOS share sheet**, which is where a member's link to this house goes out into the world under
// a name that says nothing about what she is sharing. HP6 gave every page a real URL "so the URL is
// shareable"; this is the other half of that, and it was missing for as long as the URLs have existed.
// A screen reader also reads the document title on navigation, so a route change announced nothing.
//
// The record is TOTAL (`Record<Page, string>`), which is the biconditional at the type level: a page
// added to `Page` cannot compile without a name. The gate holds these against the rosters that
// already name the same rooms — `roomLinks` and `LEGAL_DOC_TITLES` — rather than letting the house
// say two names for one room.
export const PAGE_TITLES: Record<Page, string> = {
  home: "",              // the front door wears the site's own identity; see documentTitle
  "my-room": "My Room",
  library: "Book & Video Library",
  astrology: "Astrology",
  numerology: "Numerology",
  dreams: "Dream Interpretation",
  tarot: "Tarot",
  "human-design": "Human Design",
  astrocartography: "Astrocartography",
  akashic: "Akashic Records",
  palmistry: "Palmistry",
  daily: "Daily Guidance",
  "spell-book": "Magic Room",
  seals: "Create Your Own Seals",
  "ask-lala": "Ask Lala",
  "living-room": "The Living Room",
  inbox: "Inbox",
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  faq: "Frequently Asked Questions",
  contact: "Contact",
  dashboard: "Owner Dashboard",
  membership: "Membership",
  signup: "Create your room",
  // AUTH-BREACH-1: she reaches this from her inbox, so the tab says what she came to do.
  claim: "Secure your account",
  // F1: the tab, the bookmark and — the one that matters for paid traffic — the iOS share sheet,
  // which is where a member's link to this page goes out into the world. It is deliberately the
  // HOUSE's name rather than a funnel headline: the title is written once for a page that serves
  // three doors, and a headline in the share sheet would be wrong for two of them.
  funnel: "Your Free Reading",
  // F4-1: the HOUSE’s own name rather than her hook, for the funnel’s own reason one line up — and
  // because a share sheet reading "She already knows." says nothing about where the link goes.
  //
  // >>> AND IT MAY NOT BE *Ask Lala*, WHICH IS WHAT THIS ROUND FIRST WROTE AND R42 CAUGHT IN THE
  // ROUND THAT MADE IT. <<< That is the ROOM's own title one line down, and two pages composing to
  // one document title is exactly what that census refuses: the tab, the bookmark, the history list
  // and the iOS share sheet would all name the ad door and the room she lands in identically, so a
  // member could not tell which of the two she had kept.
  talk: "Talk to Lala",
};

// This module imports nothing (the entry requirement that lets a .mjs gate RUN it), so the site's own
// name and tagline arrive as arguments rather than as a second copy of `src/siteMeta.js`. HOME is the
// front door and keeps the exact title index.html has always carried — held byte-for-byte by the gate
// against both, so the identity on the tab and the identity in the HTML can never drift apart.
export function documentTitle(page: Page, siteName: string, tagline: string): string {
  const name = PAGE_TITLES[page];
  return name ? `${name} | ${siteName}` : `${siteName}, ${tagline}`;
}
